import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Shield, Code, Image, ExternalLink, FileText, Package } from 'lucide-react';
import { patents, softwareAssets } from '../data/newContent';
import EnforcementLicensingModule from './EnforcementPresentation';

const AssetsSection = () => {
  const [showEnforcementModule, setShowEnforcementModule] = useState(false);
  
  const scrollToSection = (sectionId) => {
    const element = document.querySelector(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="assets" className="py-20 bg-slate-900 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 to-blue-900/20" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            IP & Technology <span className="text-purple-400">Assets</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Comprehensive portfolio of patents, software modules, and brand assets
          </p>
        </motion.div>

        {/* Patents Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="flex items-center gap-3 mb-8">
            <Shield className="text-purple-400" size={32} />
            <h3 className="text-3xl font-bold text-white">Patent Portfolio</h3>
          </div>

          {/* Patent Portfolio Description */}
          <div className="mb-8 bg-slate-800/50 rounded-lg p-6 border border-purple-400/20">
            <div className="space-y-4 text-gray-300 leading-relaxed">
              <p>
                IDDO foundation rests on issued world wide patents grants for multi‑structural, multi‑level information formalization. A preliminary Acoustic Intelligence set has been implemented to demonstrate and validate the fundamental advantages of MSML in real workloads; it serves as a reference pattern that can be extended or reconfigured for other modalities and data pipelines.
              </p>
              <p>
                The fortification applications describe interfaces and methods that extend the baseline into media containers, entropy and transform pathways, provenance and synchronization controls, and programmable authenticity features. Together, the core and fortification filing ready applications provide a defensible base with optional follow‑on protections aligned to client-specific roadmap and jurisdictions.
              </p>
            </div>
          </div>

          {/* Key Patents Section */}
          <div className="mb-12">
            <h4 className="text-xl font-semibold text-green-400 mb-6">Key Patents and Trademarks Grants</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {patents.filter(p => p.category === 'Key Grant').map((patent, index) => {
                const statusColors = {
                  'Granted': 'border-green-400 text-green-400',
                  'Pending': 'border-yellow-400 text-yellow-400',
                  'Filed': 'border-blue-400 text-blue-400',
                  'Filing Ready': 'border-cyan-400 text-cyan-400'
                };

                return (
                  <motion.div
                    key={patent.number + index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                    viewport={{ once: true }}
                  >
                    <Card className="bg-slate-800/50 border-purple-400/20 hover:border-purple-400/40 transition-all h-full">
                      <CardHeader>
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            {patent.displayNumber !== undefined && patent.displayNumber !== '' ? (
                              <CardTitle className="text-white text-lg mb-1">{patent.displayNumber}</CardTitle>
                            ) : null}
                            <CardDescription className="text-gray-400">
                              {patent.jurisdiction}
                            </CardDescription>
                          </div>
                          <Badge variant="outline" className={statusColors[patent.status]}>
                            {patent.status}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-gray-300 text-sm mb-4">{patent.plain_claim}</p>
                        {patent.link !== '#' && (
                          <a
                            href={patent.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 text-sm transition-colors"
                          >
                            <ExternalLink size={14} />
                            View Patent
                          </a>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Fortification Patents Section */}
          <div>
            <h4 className="text-xl font-semibold text-cyan-400 mb-6">Fortification Patent Applications: Filing Ready</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {patents.filter(p => p.category === 'Fortification').map((patent, index) => {
                const statusColors = {
                  'Granted': 'border-green-400 text-green-400',
                  'Pending': 'border-yellow-400 text-yellow-400',
                  'Filed': 'border-blue-400 text-blue-400',
                  'Filing Ready': 'border-cyan-400 text-cyan-400'
                };

                return (
                  <motion.div
                    key={patent.number + index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.05, duration: 0.5 }}
                    viewport={{ once: true }}
                  >
                    <Card className="bg-slate-800/50 border-cyan-400/20 hover:border-cyan-400/40 transition-all h-full">
                      <CardHeader>
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <CardDescription className="text-gray-400 mb-2">
                              {patent.jurisdiction}
                            </CardDescription>
                          </div>
                          <Badge variant="outline" className={statusColors[patent.status]}>
                            {patent.status}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-gray-300 text-xs leading-relaxed">{patent.plain_claim}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>

          <div className="mt-6 p-4 bg-purple-900/20 border border-purple-400/30 rounded-lg">
            <div className="flex items-start gap-2">
              <Shield className="text-purple-400 flex-shrink-0 mt-1" size={20} />
              <div>
                <p className="text-purple-300 font-semibold mb-1">Global IP Protection</p>
                <p className="text-gray-300 text-sm">
                  Multiple patent grants with worldwide coverage. Series of fortified patent applications. 
                  Legal protection reinforced by Womble Bond Dickinson (US/UK) and Canadian counsel.
                </p>
              </div>
            </div>
          </div>

          {/* Enforcement and Licensing Opportunity Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="mt-8 flex justify-center"
          >
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowEnforcementModule(true);
              }}
              className="group px-8 py-4 bg-transparent border-2 border-cyan-400/40 text-cyan-400 rounded-lg hover:bg-cyan-400/10 hover:border-cyan-400 hover:shadow-lg hover:shadow-cyan-400/20 transition-all duration-300 font-medium text-lg"
            >
              <span className="flex items-center gap-2">
                Enforcement and Licensing Opportunity
                <svg 
                  className="w-5 h-5 group-hover:translate-x-1 transition-transform" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </span>
            </button>
          </motion.div>
        </motion.div>

        {/* Enforcement Licensing Module (Modal + Presentation) */}
        <EnforcementLicensingModule 
          isOpen={showEnforcementModule}
          onClose={() => setShowEnforcementModule(false)}
        />


        {/* Software Modules Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="flex items-center gap-3 mb-8">
            <Code className="text-cyan-400" size={32} />
            <h3 className="text-3xl font-bold text-white">Software Modules & SDKs</h3>
          </div>

          <div className="grid grid-cols-1 gap-6">
            {softwareAssets.map((asset, index) => {
              const statusColors = {
                'Production': 'border-green-400 text-green-400',
                'Research': 'border-purple-400 text-purple-400',
                'Beta': 'border-blue-400 text-blue-400',
                'Alpha': 'border-yellow-400 text-yellow-400'
              };

              return (
                <motion.div
                  key={asset.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  viewport={{ once: true }}
                >
                  <Card className="bg-slate-800/50 border-cyan-400/20 hover:border-cyan-400/40 transition-all h-full">
                    <CardHeader>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Package className="text-cyan-400" size={20} />
                          <CardTitle className="text-white text-xl">{asset.name}</CardTitle>
                        </div>
                        <Badge variant="outline" className={statusColors[asset.status]}>
                          {asset.status}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <CardDescription className="text-gray-300 text-sm leading-relaxed">
                        {asset.description}
                      </CardDescription>
                      <div className="pt-4 border-t border-gray-700">
                        <p className="text-xs text-gray-500 mb-2">Supported Runtimes:</p>
                        <div className="flex flex-wrap gap-2">
                          {asset.runtimes.map((runtime, i) => (
                            <Badge key={i} variant="outline" className="border-cyan-400/30 text-cyan-400 text-xs">
                              {runtime}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Brand Assets Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="flex items-center gap-3 mb-8">
            <Image className="text-pink-400" size={32} />
            <h3 className="text-3xl font-bold text-white">Brand & IP Collateral</h3>
          </div>

          <Card className="bg-slate-800/50 border-pink-400/20">
            <CardContent className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center">
                    <FileText className="text-white" size={32} />
                  </div>
                  <h4 className="text-white font-semibold mb-2">Logo Package</h4>
                  <p className="text-gray-400 text-sm mb-3">SVG, PNG, EPS formats with usage guidelines</p>
                  <button 
                    onClick={() => scrollToSection('#contact')}
                    className="text-pink-400 hover:text-pink-300 text-sm transition-colors cursor-pointer"
                  >
                    Request →
                  </button>
                </div>

                <div className="text-center">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-400 to-pink-600 flex items-center justify-center">
                    <FileText className="text-white" size={32} />
                  </div>
                  <h4 className="text-white font-semibold mb-2">Technical Diagrams</h4>
                  <p className="text-gray-400 text-sm mb-3">Architecture and workflow visualizations</p>
                  <button 
                    onClick={() => scrollToSection('#contact')}
                    className="text-pink-400 hover:text-pink-300 text-sm transition-colors cursor-pointer"
                  >
                    Request →
                  </button>
                </div>

                <div className="text-center">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-orange-400 to-red-600 flex items-center justify-center">
                    <FileText className="text-white" size={32} />
                  </div>
                  <h4 className="text-white font-semibold mb-2">Media Kit</h4>
                  <p className="text-gray-400 text-sm mb-3">Press releases, fact sheets, and imagery</p>
                  <button 
                    onClick={() => scrollToSection('#contact')}
                    className="text-pink-400 hover:text-pink-300 text-sm transition-colors cursor-pointer"
                  >
                    Request →
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};

export default AssetsSection;
